import { CloseCircleFilled, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import useAuth from '@hooks/use-auth';
import useEffectWithPrevDeps from '@hooks/use-effect-with-prev-deps';
import useHistoryBack from '@hooks/use-history-back';
import useLastCallback from '@hooks/use-last-callback';
import type { Conversation } from '@interfaces/conversation';
import type { ApiMessage } from '@interfaces/message';
import MiddleSearchResult from '@pages/ChatFeed/MiddleColumn/MiddleSearch/MiddleSearchResult';
import buildClassName from '@utils/build-class-name';
import captureEscKeyListener from '@utils/captureEscKeyListener';
import focusEditableElement from '@utils/focus-editable-element';
import type { SearchResultKey } from '@utils/keys';
import { getSearchResultKey, parseSearchResultKey } from '@utils/keys';
import { debounce } from '@utils/schedulers';
import type { GetRef } from 'antd';
import { AutoComplete } from 'antd';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useActiveConversation, useCurrentMiddleSearch, useSharedActions } from '@/newstore/selectors/shared';

import styles from './MiddleSearch.module.scss';

type AutoCompleteRef = GetRef<typeof AutoComplete>;

interface OwnProps {
  isActive: boolean;
}

interface StateProps {
  conversation: Conversation;
  requestedQuery?: string;
  lastSearchQuery?: string;
  foundMessages?: ApiMessage[];
  fetchingQuery?: string;
}

const HIDE_TIMEOUT = 200;
const MEMO_EMPTY_ARRAY: ApiMessage[] = [];

const runDebouncedForSearch = debounce((cb) => cb(), 200, false);

const NonMemoMiddleSearch: FC<OwnProps & StateProps> = ({
  isActive,
  conversation,
  requestedQuery,
  lastSearchQuery,
  foundMessages,
  fetchingQuery,
}) => {
  const auth = useAuth();
  const { closeMiddleSearch, updateMiddleSearch, resetMiddleSearch, performMiddleSearch, selectSearchResult } =
    useSharedActions();

  const inputRef = useRef<AutoCompleteRef>(null);
  const shouldCancelSearchRef = useRef(false);

  const [query, setQuery] = useState('');

  const [isFullyHidden, setIsFullyHidden] = useState(!isActive);
  const hiddenTimerRef = useRef<number>();

  const [isLoading, setIsLoading] = useState(false);

  const handleClose = useLastCallback(() => {
    closeMiddleSearch();
  });

  const focusInput = useLastCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  });

  // Blur on exit
  useEffect(() => {
    if (!isActive) {
      inputRef.current?.blur();
      setQuery('');
      hiddenTimerRef.current = window.setTimeout(() => setIsFullyHidden(true), HIDE_TIMEOUT);
    } else {
      setIsFullyHidden(false);
      clearTimeout(hiddenTimerRef.current);
      focusInput();
    }

    return () => {
      clearTimeout(hiddenTimerRef.current);
    };
  }, [isActive, focusInput]);

  useEffect(() => {
    if (!requestedQuery || !conversation?.id) {
      return;
    }
    setQuery(requestedQuery);
    updateMiddleSearch(conversation.id, { requestedQuery: undefined });
    setIsLoading(true);

    const input = inputRef.current;
    if (!input) {
      return;
    }
    focusEditableElement(input.nativeElement, true, true);
  }, [conversation.id, requestedQuery, updateMiddleSearch]);

  useEffectWithPrevDeps(
    ([prevIsActive]) => {
      if (isActive !== prevIsActive && !query && lastSearchQuery) {
        setQuery(lastSearchQuery); // Restore query when returning back
      }
    },
    [isActive, lastSearchQuery, query],
  );

  const handleReset = useLastCallback(() => {
    if (!query.length) {
      handleClose();
      return;
    }

    setQuery('');
    setIsLoading(false);
    resetMiddleSearch();
    focusInput();
  });

  useEffect(() => (isActive ? captureEscKeyListener(handleReset) : undefined), [isActive, handleReset]);

  const handleSearch = useLastCallback(() => {
    const conversationId = conversation.id;

    runDebouncedForSearch(() => {
      if (shouldCancelSearchRef.current) {
        return;
      }
      // noinspection JSIgnoredPromiseFromCall
      performMiddleSearch(auth, conversationId, query);
    });
  });

  const handleQueryChange = useLastCallback((newQuery: string) => {
    shouldCancelSearchRef.current = false;
    setQuery(newQuery);

    if (!newQuery) {
      setIsLoading(false);
      resetMiddleSearch();
      shouldCancelSearchRef.current = true;
    }
  });

  const handleMessageSelect = useLastCallback((value: string) => {
    const [conversationId, messageId] = parseSearchResultKey(value as SearchResultKey);
    // noinspection JSIgnoredPromiseFromCall
    selectSearchResult(auth, conversationId, messageId);
  });

  useEffect(() => {
    if (isActive && query) {
      handleSearch();
    }
  }, [isActive, query, handleSearch]);

  useEffect(() => {
    setIsLoading(Boolean(fetchingQuery));
  }, [fetchingQuery]);

  useHistoryBack(isActive, handleClose);

  const viewportResults = useMemo(() => {
    if (!query || !foundMessages || foundMessages.length === 0) {
      return MEMO_EMPTY_ARRAY;
    }

    return foundMessages;
  }, [query, foundMessages]);

  const renderItem = useCallback(
    (message: ApiMessage) => ({
      value: getSearchResultKey(message),
      label: <MiddleSearchResult key={getSearchResultKey(message)} message={message} query={query} />,
    }),
    [query],
  );

  const renderedItems = useMemo(
    () => viewportResults.map((result) => renderItem(result)),
    [renderItem, viewportResults],
  );

  return (
    <div
      className={buildClassName(
        styles.root,
        isActive && styles.active,
        !isActive && isFullyHidden && 'visually-hidden',
      )}
      id="middle-search"
    >
      <div className={styles.header}>
        {/* TODO: replace autocomplete with custom component, to customize dropdown view and behavior */}
        <AutoComplete<string>
          ref={inputRef}
          value={query}
          className={styles.input}
          classNames={{
            popup: {
              root: styles.popup,
            },
          }}
          aria-hidden={!isActive}
          placeholder="Search"
          options={renderedItems}
          prefix={isLoading ? <LoadingOutlined /> : <SearchOutlined />}
          suffixIcon={<CloseCircleFilled onClick={handleReset} />}
          onSearch={handleQueryChange}
          onSelect={handleMessageSelect}
          notFoundContent={`No results found for "${query}"`}
        />
      </div>
    </div>
  );
};

const MiddleSearch = memo(function MiddleSearch({ isActive }: OwnProps) {
  const { conversation } = useActiveConversation();
  const searchQuery = useCurrentMiddleSearch();
  const { requestedQuery, fetchingQuery, results } = searchQuery || {};
  const { query: lastSearchQuery, foundMessages } = results || {};

  if (!conversation) {
    return null;
  }

  return (
    <NonMemoMiddleSearch
      isActive={isActive}
      conversation={conversation}
      requestedQuery={requestedQuery}
      foundMessages={foundMessages}
      fetchingQuery={fetchingQuery}
      lastSearchQuery={lastSearchQuery}
    />
  );
});

export default MiddleSearch;
